import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo, { itemListJsonLd, breadcrumbJsonLd } from "../components/Seo";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [], colors: [] });
  const [loading, setLoading] = useState(true);

  const filters = useMemo(
    () => ({
      category: params.get("category") || "",
      brand: params.get("brand") || "",
      color: params.get("color") || "",
      q: params.get("q") || "",
      sort: params.get("sort") || "newest",
      min_price: params.get("min_price") || "",
      max_price: params.get("max_price") || "",
    }),
    [params]
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/products/meta/filters");
        setMeta(r.data);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/products", {
          params: {
            category: filters.category || undefined,
            brand: filters.brand || undefined,
            color: filters.color || undefined,
            q: filters.q || undefined,
            min_price: filters.min_price || undefined,
            max_price: filters.max_price || undefined,
            sort: filters.sort,
            limit: 1000,
          },
        });
        setProducts(r.data || []);
      } catch (_) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next);
  };

  // Update multiple URL params at once (e.g. price brackets that set both
  // min_price and max_price). Pass null/empty string to remove a key.
  const setMultipleFilters = (updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next);
  };

  const clearAll = () => setParams({});

  // Build dynamic SEO title/description based on active filters.
  const priceLabel = (() => {
    const min = Number(filters.min_price) || 0;
    const max = Number(filters.max_price) || 0;
    if (min && max) return `$${min.toLocaleString()}–$${max.toLocaleString()}`;
    if (max) return `under $${max.toLocaleString()}`;
    if (min) return `over $${min.toLocaleString()}`;
    return "";
  })();
  const seoBits = [filters.brand, filters.category, filters.color].filter(Boolean);
  const seoTitle = filters.q
    ? `Search: ${filters.q}`
    : seoBits.length || priceLabel
    ? `${[...seoBits, "Bags", priceLabel].filter(Boolean).join(" ")}`
    : "Shop the Collection";
  const seoDesc = filters.brand
    ? `Authentic ${filters.brand} ${filters.category || "designer"} bags${filters.color ? ` in ${filters.color}` : ""}${priceLabel ? ` ${priceLabel}` : ""}. Hand-curated by Elegant Affairs. Free shipping over $200.`
    : (filters.category || priceLabel)
    ? `Designer luxury ${filters.category ? filters.category.toLowerCase() + " " : ""}bags${priceLabel ? ` ${priceLabel}` : ""} from 40+ houses. Hand-authenticated. Free shipping over $200.`
    : "Browse 450 hand-curated designer luxury bags from Saint Laurent, Versace, Gucci, Balenciaga, Prada, and 40+ houses. Authenticated. Free shipping over $200.";

  // Build structured data for SERP: BreadcrumbList + ItemList of currently visible products.
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Shop", url: "/shop" },
    ...(filters.brand ? [{ name: filters.brand }] : []),
  ];
  const listName = seoBits.length ? `${seoBits.join(" ")} Bags` : "Designer Luxury Bags";
  const itemList = itemListJsonLd(products, listName);
  const breadcrumb = breadcrumbJsonLd(breadcrumbs);
  const shopJsonLd = itemList
    ? { "@context": "https://schema.org", "@graph": [breadcrumb, itemList] }
    : breadcrumb;

  return (
    <div className="pt-32 pb-24" data-testid="shop-page">
      <Seo title={seoTitle} description={seoDesc} jsonLd={shopJsonLd} />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow">Collection</p>
            <h1 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">The Catalogue.</h1>
            {filters.q && (
              <p className="mt-3 font-display italic text-xl text-[var(--mn-ink-2)]">
                Searching: "{filters.q}"
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="eyebrow">Sort</label>
            <select
              value={filters.sort}
              onChange={(e) => setFilter("sort", e.target.value)}
              className="bg-transparent border-b border-[var(--mn-border-strong)] py-2 pr-6 text-sm focus:outline-none focus:border-[var(--mn-ink)]"
              data-testid="shop-sort-select"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <QuickFilters filters={filters} meta={meta} apply={setMultipleFilters} />

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Filters */}
          <aside className="col-span-12 md:col-span-3" data-testid="shop-filters">
            <FilterGroup
              title="Category"
              options={meta.categories}
              value={filters.category}
              onSelect={(v) => setFilter("category", v)}
              testid="filter-category"
            />
            <FilterGroup
              title="Brand"
              options={meta.brands}
              value={filters.brand}
              onSelect={(v) => setFilter("brand", v)}
              testid="filter-brand"
            />
            <FilterGroup
              title="Colour"
              options={meta.colors}
              value={filters.color}
              onSelect={(v) => setFilter("color", v)}
              testid="filter-color"
            />
            {(filters.category || filters.brand || filters.color || filters.q || filters.min_price || filters.max_price) && (
              <button
                onClick={clearAll}
                className="mt-6 text-xs uppercase tracking-[0.22em] underline underline-offset-4 hover:text-[var(--mn-accent)]"
                data-testid="clear-filters"
              >
                Clear all
              </button>
            )}
          </aside>

          {/* Products */}
          <div className="col-span-12 md:col-span-9">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-neutral-200/60 animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="border border-dashed border-[var(--mn-border-strong)] py-24 text-center" data-testid="shop-empty">
                <p className="font-display text-3xl">No pieces match your filters.</p>
                <button onClick={clearAll} className="btn-ghost mt-6" data-testid="shop-empty-clear">
                  Reset filters
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-[var(--mn-ink-2)] uppercase tracking-[0.22em] mb-6" data-testid="shop-count">
                  {products.length} piece{products.length === 1 ? "" : "s"}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10" data-testid="product-grid">
                  {products.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, options, value, onSelect, testid }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="mb-10" data-testid={testid}>
      <p className="eyebrow mb-4">{title}</p>
      <ul className="space-y-2">
        {options.map((o) => {
          const active = value === o;
          return (
            <li key={o}>
              <button
                onClick={() => onSelect(active ? "" : o)}
                className={`text-left text-sm transition-colors ${
                  active ? "text-[var(--mn-accent)] font-medium" : "text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
                }`}
                data-testid={`${testid}-${String(o).toLowerCase()}`}
              >
                {active ? "— " : ""}{o}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -----------------------------
// Quick-filter chip row
// -----------------------------
// Curated, one-click presets that drive the same URL params as the sidebar.
// Designed so a visitor can land on a meaningful subset of the catalogue in
// one tap, and each preset has its own canonical URL for SEO.
const PRICE_PRESETS = [
  { label: "Under $500", min: "", max: "500", testid: "price-under-500", bucket: "under_500" },
  { label: "$500–$1k", min: "500", max: "1000", testid: "price-500-1k", bucket: "500_1000" },
  { label: "$1k–$3k", min: "1000", max: "3000", testid: "price-1k-3k", bucket: "1000_3000" },
  { label: "$3k–$5k", min: "3000", max: "5000", testid: "price-3k-5k", bucket: "3000_5000" },
  { label: "$5k+", min: "5000", max: "", testid: "price-5k-plus", bucket: "5000_plus" },
];
// Up-front houses & styles. We intersect with whatever meta returns so we
// never show a chip that produces zero results.
const HOUSE_CHIPS = ["Saint Laurent", "Gucci", "Prada", "Dior", "Chanel", "Bottega Veneta"];
const STYLE_CHIPS = ["Crossbody", "Tote", "Shoulder", "Clutch", "Handbag"];

function QuickFilters({ filters, meta, apply }) {
  const availableHouses = HOUSE_CHIPS.filter((b) => (meta.brands || []).includes(b));
  const availableStyles = STYLE_CHIPS.filter((c) => (meta.categories || []).includes(c));
  const bracketCounts = meta.price_brackets || {};
  // Show a price chip only when it has inventory — keeps the UI honest while
  // new tiers (e.g. sub-$500) are being seeded.
  const availablePricePresets = PRICE_PRESETS.filter((p) => (bracketCounts[p.bucket] ?? 1) > 0);

  const pricePresetActive = (preset) =>
    String(filters.min_price || "") === preset.min &&
    String(filters.max_price || "") === preset.max;

  return (
    <div className="mb-10 border-y border-[var(--mn-border)] py-5" data-testid="shop-quick-filters">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 overflow-x-auto no-scrollbar">
        <ChipLabel>Price</ChipLabel>
        {availablePricePresets.map((p) => {
          const active = pricePresetActive(p);
          return (
            <Chip
              key={p.label}
              active={active}
              onClick={() =>
                apply({
                  min_price: active ? "" : p.min,
                  max_price: active ? "" : p.max,
                })
              }
              testid={p.testid}
            >
              {p.label}
            </Chip>
          );
        })}

        {availableStyles.length > 0 && (
          <>
            <Divider />
            <ChipLabel>Style</ChipLabel>
            {availableStyles.map((c) => {
              const active = filters.category === c;
              return (
                <Chip
                  key={c}
                  active={active}
                  onClick={() => apply({ category: active ? "" : c })}
                  testid={`style-${c.toLowerCase()}`}
                >
                  {c}
                </Chip>
              );
            })}
          </>
        )}

        {availableHouses.length > 0 && (
          <>
            <Divider />
            <ChipLabel>House</ChipLabel>
            {availableHouses.map((b) => {
              const active = filters.brand === b;
              return (
                <Chip
                  key={b}
                  active={active}
                  onClick={() => apply({ brand: active ? "" : b })}
                  testid={`house-${b.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {b}
                </Chip>
              );
            })}
          </>
        )}

        <Divider />
        <Chip
          active={filters.sort === "newest" && !filters.category && !filters.brand && !filters.min_price && !filters.max_price && !filters.color && !filters.q}
          onClick={() =>
            apply({
              sort: "newest",
              category: "",
              brand: "",
              color: "",
              q: "",
              min_price: "",
              max_price: "",
            })
          }
          testid="quick-just-arrived"
        >
          ✦ Just Arrived
        </Chip>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 text-[11px] uppercase tracking-[0.18em] px-3.5 py-2 border transition-colors whitespace-nowrap ${
        active
          ? "bg-[var(--mn-ink)] text-[var(--mn-bg)] border-[var(--mn-ink)]"
          : "border-[var(--mn-border-strong)] text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)] hover:border-[var(--mn-ink)]"
      }`}
    >
      {children}
    </button>
  );
}

function ChipLabel({ children }) {
  return (
    <span className="shrink-0 text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] font-medium">
      {children}
    </span>
  );
}

function Divider() {
  return <span className="hidden md:inline-block h-5 w-px bg-[var(--mn-border-strong)] mx-1" />;
}
